import Analytics from '@/utils/analytics';
import React, { useCallback, useMemo } from 'react';
import { AD_VIEWABILITY_CONFIG, useAdsContext } from '@/contexts/ads-context';
import { AdImpressionSource } from '@/contexts/ads-context-types';
import {
  DEFAULT_TIMEOUT_MINUTES,
  useImpressionContext,
} from '@/contexts/impression-context';
import {
  ItemListOption,
  ItemListOptionMetadata,
  ItemListOptionSection,
} from '@/contexts/item-context-types';
import {
  Keyboard,
  ScrollViewProps,
  StyleProp,
  ViewStyle,
  ViewToken,
} from 'react-native';
import {
  ListCarouselOption,
  ListItemType,
  ListSection,
  Props as RecyclerListProps,
  RecyclerListRef,
} from '@/components/recycler-list';
import {
  Props as ListItemProps,
  ListOptionValue,
} from '@/components/list-item';
import {
  OrderableItemList,
  useOrderableItemColumnCount,
} from './orderable-item-list';
import { OrderGuideCarouselType } from '@/contexts/dynamic-config';
import { OrderGuideListItem } from './order-guide-list-item';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useShowListView } from '@/hooks/use-show-list-view';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';
import { useUserContext } from '@/contexts/user-context';

export type OrderGuideItemListOption = ItemListOption & {
  hideTrailingItemSeparator?: boolean;
};

export type OrderGuideCarouselOption = ListCarouselOption<{
  position: number;
  renderCarousel: () => React.ReactNode;
  type: OrderGuideCarouselType;
}>;

type OrderGuideListOption = OrderGuideItemListOption | OrderGuideCarouselOption;

export type OrderGuideItemSection = ListSection<OrderGuideListOption>;

interface OrderGuideListProps {
  disableTextInputForAllListItems?: boolean;
  highlightedItemValues?: ListOptionValue[];
  isFocused: boolean;
  itemStyleOverrides?: StyleProp<ViewStyle>;
  listContentContainerStyle?: StyleProp<ViewStyle>;
  ListHeaderComponent?: RecyclerListProps<ItemListOption>['ListHeaderComponent'];
  listItemProps?: Partial<ListItemProps<ItemListOption>>;
  listRef?: React.Ref<RecyclerListRef<ItemListOption>>;
  onVisible?: () => void;
  renderSectionHeader?: RecyclerListProps<ItemListOption>['renderSectionHeader'];
  scrollViewProps?: ScrollViewProps;
  sections: ItemListOptionSection[];
  setDSRToolingActionSheetItemMetadata?: (sku: ItemListOptionMetadata) => void;
  showSeparatorBetweenSections?: boolean;
  showTooltipOnMount?: boolean;
}

export const OrderGuideList: React.FC<OrderGuideListProps> = ({
  disableTextInputForAllListItems,
  highlightedItemValues,
  isFocused,
  itemStyleOverrides,
  listContentContainerStyle,
  ListHeaderComponent,
  listItemProps: parentListItemProps = {},
  listRef,
  onVisible,
  renderSectionHeader,
  scrollViewProps,
  sections,
  setDSRToolingActionSheetItemMetadata,
  showSeparatorBetweenSections,
  showTooltipOnMount,
}) => {
  const { selectItemListOption } = useItemDetailContext();
  const { isItemMinimized } = useUpdateOrderGuideItemContext();
  const { onViewableAdItemsChanged } = useAdsContext();
  const { fireDedupedImpressionEvent } = useImpressionContext();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const defaultColumnCount = useOrderableItemColumnCount();

  const { showListView } = useShowListView();
  const listItemProps = useMemo<Partial<ListItemProps<ItemListOption>>>(() => {
    return {
      onSelect: (option) => {
        Keyboard.dismiss();
        selectItemListOption(option, { source: 'order_guide' });
      },
      renderMainContent: (option) => {
        return (
          <OrderGuideListItem
            disableTextInput={disableTextInputForAllListItems}
            isFocused={isFocused}
            itemStyleOverrides={itemStyleOverrides}
            option={option}
            setDSRToolingActionSheetItemMetadata={
              setDSRToolingActionSheetItemMetadata
            }
            showTooltipOnMount={showTooltipOnMount}
          />
        );
      },
      ...parentListItemProps,
    };
  }, [
    disableTextInputForAllListItems,
    isFocused,
    itemStyleOverrides,
    parentListItemProps,
    selectItemListOption,
    setDSRToolingActionSheetItemMetadata,
    showTooltipOnMount,
  ]);

  const onViewableCarouselsChanged = useCallback(
    (viewableItems: ViewToken[]) => {
      for (const viewableItem of viewableItems) {
        const listItem = viewableItem.item as OrderGuideListOption;
        if (listItem.value === ListItemType.Carousel) {
          const metadata = (listItem as OrderGuideCarouselOption).metadata!;
          const shouldRender = Boolean(metadata.renderCarousel());

          if (shouldRender) {
            const impressionEventKey = [metadata.type, metadata.position].join(
              '-',
            );
            fireDedupedImpressionEvent(
              impressionEventKey,
              () => {
                Analytics.track('order_guide_carousels.impression', {
                  position: metadata.position,
                  restaurantUUID,
                  supplierUUID,
                  type: metadata.type,
                });
              },
              DEFAULT_TIMEOUT_MINUTES,
            );
          }
        }
      }
    },
    [fireDedupedImpressionEvent, restaurantUUID, supplierUUID],
  );

  return (
    <OrderableItemList
      highlightedItemValues={highlightedItemValues}
      listContentContainerStyle={listContentContainerStyle}
      ListHeaderComponent={ListHeaderComponent}
      listItemProps={listItemProps}
      listRef={listRef}
      onViewableItemsChanged={({ viewableItems }) => {
        onViewableAdItemsChanged(viewableItems, AdImpressionSource.OrderGuide);
        onViewableCarouselsChanged(viewableItems);
      }}
      onVisible={onVisible}
      renderSectionHeader={renderSectionHeader}
      scrollViewProps={scrollViewProps}
      sections={sections}
      shouldRenderSeparator={(
        leadingItem: OrderGuideItemListOption,
        trailingItem: OrderGuideItemListOption,
      ) => {
        // Hide list item separators for single columns
        if (defaultColumnCount === 1) {
          return false;
        }

        const leadingMetadata = leadingItem.metadata;
        const trailingMetadata = trailingItem.metadata;
        if (!leadingMetadata || !trailingMetadata) {
          return true;
        }

        if (leadingItem.hideTrailingItemSeparator) {
          return false;
        }

        const isLeadingMinimized = isItemMinimized(leadingMetadata);
        const isTrailingMinimized = isItemMinimized(trailingMetadata);

        return !isLeadingMinimized && !isTrailingMinimized;
      }}
      showListView={showListView}
      showSeparatorBetweenSections={showSeparatorBetweenSections}
      viewabilityConfig={AD_VIEWABILITY_CONFIG}
    />
  );
};
